import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium placeholder:font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-r !text-white border-t-[1.5px] border-solid border-white/30 active:opacity-80 from-[#0C7487] to-[#059DB8] text-primary-foreground shadow hover:bg-gradient-to-r hover:from-[#0C7487]/90 hover:to-[#059DB8]/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-white/[.33] hover:border-white/[.66] active:border-white/[.44] text-white",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "active:bg-accent active:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        dark: "!text-white border-t-[1.5px] border-solid border-white/30 active:opacity-80 bg-[#6C7172] text-white shadow hover:bg-[#6C7172]/90",
      },
      size: {
        default: "px-[10px] py-[10px] text-base leading-4 h-[38px]",
        sm: "h-8 px-3 text-xs",
        lg: "h-[43px] px-3",
        icon: "h-7 w-7",
        "icon-sm": "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isPending?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      type = "button",
      children,
      isPending = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        type={type}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
        {isPending ? <Loader2 size={16} className="animate-spin" /> : null}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
